import type { FieldValues, Path } from 'react-hook-form';
import { Controller, useFormContext, useFormState } from 'react-hook-form';

import AddressTextField, {
  type AddressTextFieldProps,
} from '../AddressTextField/AddressTextField';
import type { AddressTextFieldBaseProps } from '../AddressTextField/AddressTextFieldBase';
import type { PlaceResult } from '../AddressTextField/types';

type PresetProps =
  | 'value'
  | 'onValueChange'
  | 'onChange'
  | 'onBlur'
  | 'name'
  | 'error'
  | 'helperText'
  | 'googleMapsApiKey';

export type RhfAddressFieldProps<T extends FieldValues> = Omit<
  AddressTextFieldProps,
  PresetProps
> & {
  name: Path<T>;
  googleMapsApiKey: string;
  helperText?: AddressTextFieldProps['helperText'];
  onBlur?: AddressTextFieldBaseProps['onBlur'];
  onInputChange?: AddressTextFieldBaseProps['onInputChange'];
  onPlaceSelected?: (place: PlaceResult) => void;
};

export default function RhfAddressField<T extends FieldValues>({
  name,
  helperText,
  onBlur: onBlurFromProps,
  onInputChange,
  onPlaceSelected,
  ...rest
}: RhfAddressFieldProps<T>) {
  const { control, trigger } = useFormContext<T>();
  const { isSubmitted } = useFormState({ control });

  return (
    <Controller<T>
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const errorMessage =
          typeof fieldState.error?.message === 'string'
            ? fieldState.error.message
            : undefined;
        // Only surface the error once the user has left the field (or
        // submitted), so it doesn't flash red while they're still typing
        // and haven't had a chance to pick a suggestion.
        const shouldShowError =
          Boolean(fieldState.error) && (fieldState.isTouched || isSubmitted);

        return (
          <AddressTextField
            {...rest}
            name={field.name}
            value={typeof field.value === 'string' ? field.value : ''}
            onValueChange={field.onChange}
            onBlur={(event) => {
              field.onBlur();
              void trigger(name);
              onBlurFromProps?.(event);
            }}
            onInputChange={onInputChange}
            onPlaceSelected={onPlaceSelected}
            error={shouldShowError}
            helperText={shouldShowError ? errorMessage : helperText}
          />
        );
      }}
    />
  );
}
